// TV portfolio overlay launched from the studio TV object.
// Streaming-service-style modal with rows of video tiles by category.
// Tap a tile → tile grid swaps for an inline video player + back button.
// ESC closes from any state. Reads VIDEO_PORTFOLIO + VIDEO_PORTFOLIO_CATEGORIES
// from world.config.js. v1 ships a single Featured row.
//
// Hosting agnostic — `embedKind` switches between 'mp4' (R2 / self-hosted),
// 'vimeo', 'youtube', 'link'. See V1-STUDIO-SCOPE.md "TV / video portfolio".

import { VIDEO_PORTFOLIO, VIDEO_PORTFOLIO_CATEGORIES } from '../world.config.js';

function makeCloseBtn(onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Close');
  btn.textContent = '×';
  btn.style.cssText = `
    background: transparent;
    border: none;
    color: rgba(244, 234, 213, 0.5);
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 8px;
    margin: 0;
    font-family: inherit;
    transition: color 140ms ease;
  `;
  btn.addEventListener('mouseenter', () => { btn.style.color = '#f4ead5'; });
  btn.addEventListener('mouseleave', () => { btn.style.color = 'rgba(244, 234, 213, 0.5)'; });
  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return btn;
}

export class TVPortfolioSystem {
  constructor({ onOpen, onClose } = {}) {
    this.onOpen = onOpen ?? null;
    this.onClose = onClose ?? null;
    this.scrim = null;
    this.panel = null;
    this.gridEl = null;
    this.playerEl = null;
    this.isOpen = false;
    this._keyHandler = null;
    this._mount();
  }

  _mount() {
    const existing = document.querySelector('.iw-tv-scrim');
    if (existing) {
      this.scrim = existing;
      this.panel = existing.querySelector('.iw-tv-panel');
      this.gridEl = existing.querySelector('.iw-tv-grid');
      this.playerEl = existing.querySelector('.iw-tv-player');
      this.scrim.style.display = 'none';
      return;
    }

    const scrim = document.createElement('div');
    scrim.className = 'iw-tv-scrim';
    scrim.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(8, 6, 5, 0.86);
      backdrop-filter: blur(3px);
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 70;
      pointer-events: auto;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      color: #f4ead5;
    `;

    const panel = document.createElement('div');
    panel.className = 'iw-tv-panel';
    panel.style.cssText = `
      width: min(960px, 92vw);
      max-height: 88vh;
      background: #0e0c0a;
      border: 1px solid rgba(244, 234, 213, 0.18);
      border-radius: 12px;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `;

    // Header — title + close hint
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px 14px;
      border-bottom: 1px solid rgba(244, 234, 213, 0.08);
    `;
    const title = document.createElement('div');
    title.textContent = "Ian's screen";
    title.style.cssText = `
      font-size: 14px;
      letter-spacing: 0.06em;
      text-transform: lowercase;
      color: #f4ead5;
    `;
    const closeHint = document.createElement('div');
    closeHint.textContent = 'esc to close';
    closeHint.style.cssText = `
      font-size: 11px;
      letter-spacing: 0.06em;
      color: rgba(244, 234, 213, 0.45);
    `;
    const headerRight = document.createElement('div');
    headerRight.style.cssText = `display: flex; align-items: center; gap: 12px;`;
    headerRight.appendChild(closeHint);
    headerRight.appendChild(makeCloseBtn(() => this.close()));
    header.appendChild(title);
    header.appendChild(headerRight);

    // Grid view (default state) — categorized rows of tiles
    const grid = document.createElement('div');
    grid.className = 'iw-tv-grid';
    grid.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 18px 20px 24px;
      display: flex;
      flex-direction: column;
      gap: 22px;
    `;

    // Player view (hidden by default) — shown when a tile is tapped
    const player = document.createElement('div');
    player.className = 'iw-tv-player';
    player.style.cssText = `
      flex: 1;
      display: none;
      flex-direction: column;
      padding: 16px 20px 20px;
      gap: 12px;
    `;

    panel.appendChild(header);
    panel.appendChild(grid);
    panel.appendChild(player);
    scrim.appendChild(panel);
    document.body.appendChild(scrim);

    // Click outside the panel closes (consistent with Phone / Mail / Modal)
    scrim.addEventListener('click', (event) => {
      if (event.target === scrim) this.close();
    });

    this.scrim = scrim;
    this.panel = panel;
    this.gridEl = grid;
    this.playerEl = player;
  }

  open() {
    this.onOpen?.();
    this.isOpen = true;
    this.scrim.style.display = 'flex';
    this._renderGrid();
    this._showGrid();
    this._bindKeys();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._unbindKeys();
    this._stopPlayback();
    this.scrim.style.display = 'none';
    this.onClose?.();
  }

  _showGrid() {
    this.gridEl.style.display = 'flex';
    this.playerEl.style.display = 'none';
  }

  _showPlayer() {
    this.gridEl.style.display = 'none';
    this.playerEl.style.display = 'flex';
  }

  _renderGrid() {
    this.gridEl.innerHTML = '';

    if (!VIDEO_PORTFOLIO || VIDEO_PORTFOLIO.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        gap: 10px;
        text-align: center;
      `;
      const big = document.createElement('div');
      big.textContent = 'videos coming soon';
      big.style.cssText = `
        font-size: 18px;
        letter-spacing: 0.08em;
        color: rgba(244, 234, 213, 0.7);
        text-transform: lowercase;
      `;
      const sub = document.createElement('div');
      sub.textContent = "I'll start dropping clips in here as I curate them.";
      sub.style.cssText = `
        font-size: 12px;
        color: rgba(244, 234, 213, 0.4);
        max-width: 340px;
        line-height: 1.5;
      `;
      empty.appendChild(big);
      empty.appendChild(sub);
      this.gridEl.appendChild(empty);
      return;
    }

    // Render rows by category, in the order defined in VIDEO_PORTFOLIO_CATEGORIES.
    // Skip categories with no entries.
    for (const category of VIDEO_PORTFOLIO_CATEGORIES) {
      const items = VIDEO_PORTFOLIO.filter((v) => v.category === category.id);
      if (items.length === 0) continue;
      this.gridEl.appendChild(this._makeRow(category, items));
    }
  }

  _makeRow(category, items) {
    this._ensureScrollerStyles();

    const row = document.createElement('div');
    row.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;

    const label = document.createElement('div');
    label.textContent = category.label;
    label.style.cssText = `
      font-size: 11px;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: rgba(244, 234, 213, 0.55);
    `;

    // Arrows live in a relative wrapper so they can overlay the scroller edges.
    const scrollerWrap = document.createElement('div');
    scrollerWrap.style.cssText = `position: relative;`;

    const tiles = document.createElement('div');
    tiles.className = 'iw-tv-row-scroller';
    tiles.style.cssText = `
      display: flex;
      gap: 12px;
      overflow-x: auto;
      scroll-behavior: smooth;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding: 2px 2px 4px;
    `;

    items.forEach((item, idx) => {
      const tile = this._makeTile(item, idx);
      // Fixed width so tiles don't shrink under flex; height stretches to row max.
      tile.style.flex = '0 0 220px';
      tiles.appendChild(tile);
    });

    const leftArrow = this._makeScrollArrow('left', tiles);
    const rightArrow = this._makeScrollArrow('right', tiles);

    scrollerWrap.appendChild(tiles);
    scrollerWrap.appendChild(leftArrow);
    scrollerWrap.appendChild(rightArrow);

    // Show/hide arrows based on whether there's more to scroll in that direction.
    const updateArrows = () => {
      const canLeft = tiles.scrollLeft > 4;
      const canRight = tiles.scrollLeft < tiles.scrollWidth - tiles.clientWidth - 4;
      leftArrow.style.opacity = canLeft ? '1' : '0';
      leftArrow.style.pointerEvents = canLeft ? 'auto' : 'none';
      rightArrow.style.opacity = canRight ? '1' : '0';
      rightArrow.style.pointerEvents = canRight ? 'auto' : 'none';
    };
    tiles.addEventListener('scroll', updateArrows);
    requestAnimationFrame(updateArrows);
    // Re-evaluate on resize so arrows hide when the row becomes wide enough.
    window.addEventListener('resize', updateArrows);

    row.appendChild(label);
    row.appendChild(scrollerWrap);
    return row;
  }

  _makeScrollArrow(direction, scroller) {
    const arrow = document.createElement('button');
    arrow.type = 'button';
    arrow.textContent = direction === 'left' ? '‹' : '›';
    arrow.setAttribute('aria-label', direction === 'left' ? 'Scroll left' : 'Scroll right');
    arrow.style.cssText = `
      position: absolute;
      top: calc(50% - 18px);
      ${direction}: -10px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(15, 12, 10, 0.9);
      border: 1px solid rgba(244, 234, 213, 0.22);
      color: #f4ead5;
      font-size: 22px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 180ms ease, background 140ms ease;
      z-index: 2;
      font-family: inherit;
      padding: 0 0 2px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
    `;
    arrow.addEventListener('mouseenter', () => {
      arrow.style.background = 'rgba(28, 22, 18, 0.95)';
    });
    arrow.addEventListener('mouseleave', () => {
      arrow.style.background = 'rgba(15, 12, 10, 0.9)';
    });
    arrow.addEventListener('click', (event) => {
      event.stopPropagation();
      const delta = scroller.clientWidth * 0.8;
      scroller.scrollBy({ left: direction === 'left' ? -delta : delta, behavior: 'smooth' });
    });
    return arrow;
  }

  _ensureScrollerStyles() {
    if (document.getElementById('iw-tv-scroller-style')) return;
    const style = document.createElement('style');
    style.id = 'iw-tv-scroller-style';
    style.textContent = `.iw-tv-row-scroller::-webkit-scrollbar { display: none; }`;
    document.head.appendChild(style);
  }

  _makeTile(item, idx = 0) {
    // Placeholder tiles (e.g. "Coming Soon" cards in a row that's not yet
    // populated with real content) render normally but don't fire a click
    // handler — visitors can see them but they're not interactive yet.
    const isPlaceholder = item.embedKind === 'placeholder';
    // First 4 tiles per row are visible without scrolling; load eagerly with
    // high priority. Tiles beyond that lazy-load as the user scrolls them in.
    const isAboveFold = idx < 4;

    const tile = document.createElement('button');
    tile.type = 'button';
    if (isPlaceholder) tile.disabled = true;
    tile.style.cssText = `
      background: transparent;
      border: 1px solid rgba(244, 234, 213, 0.12);
      border-radius: 8px;
      padding: 0;
      overflow: hidden;
      cursor: ${isPlaceholder ? 'default' : 'pointer'};
      display: flex;
      flex-direction: column;
      gap: 0;
      font-family: inherit;
      color: #f4ead5;
      opacity: ${isPlaceholder ? '0.62' : '1'};
      transition: transform 140ms ease, border-color 140ms ease;
    `;

    const thumb = document.createElement('div');
    thumb.style.cssText = `
      aspect-ratio: 16 / 9;
      background: linear-gradient(135deg, #2a1f18, #4a2f1c);
      position: relative;
      overflow: hidden;
    `;
    if (item.thumbnail) {
      const img = document.createElement('img');
      img.src = item.thumbnail;
      img.alt = item.title || '';
      img.loading = isAboveFold ? 'eager' : 'lazy';
      img.decoding = 'async';
      if (isAboveFold) img.fetchPriority = 'high';
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      `;
      thumb.appendChild(img);
    }
    if (item.duration) {
      const dur = document.createElement('span');
      dur.textContent = item.duration;
      dur.style.cssText = `
        position: absolute;
        right: 6px;
        bottom: 6px;
        padding: 2px 6px;
        background: rgba(8, 6, 5, 0.78);
        border-radius: 4px;
        font-size: 10px;
        letter-spacing: 0.04em;
        color: #f4ead5;
      `;
      thumb.appendChild(dur);
    }

    const meta = document.createElement('div');
    meta.style.cssText = `
      padding: 10px 12px 12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      text-align: left;
    `;
    const t = document.createElement('div');
    t.textContent = item.title;
    t.style.cssText = `
      font-size: 13px;
      color: #f4ead5;
      line-height: 1.3;
    `;
    meta.appendChild(t);
    if (item.description) {
      const d = document.createElement('div');
      d.textContent = item.description;
      d.style.cssText = `
        font-size: 11px;
        color: rgba(244, 234, 213, 0.55);
        line-height: 1.4;
      `;
      meta.appendChild(d);
    }

    tile.appendChild(thumb);
    tile.appendChild(meta);

    if (!isPlaceholder) {
      tile.addEventListener('mouseenter', () => {
        tile.style.borderColor = 'rgba(244, 234, 213, 0.32)';
        tile.style.transform = 'translateY(-2px)';
      });
      tile.addEventListener('mouseleave', () => {
        tile.style.borderColor = 'rgba(244, 234, 213, 0.12)';
        tile.style.transform = 'translateY(0)';
      });

      tile.addEventListener('click', () => this._playVideo(item));
    }

    return tile;
  }

  _playVideo(item) {
    this.playerEl.innerHTML = '';

    // Back button — top-left of player view
    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = '← back';
    back.style.cssText = `
      align-self: flex-start;
      background: transparent;
      border: 1px solid rgba(244, 234, 213, 0.2);
      border-radius: 999px;
      padding: 6px 14px;
      font-family: inherit;
      font-size: 11px;
      letter-spacing: 0.08em;
      color: #f4ead5;
      cursor: pointer;
      transition: background 140ms ease;
    `;
    back.addEventListener('mouseenter', () => { back.style.background = 'rgba(244, 234, 213, 0.08)'; });
    back.addEventListener('mouseleave', () => { back.style.background = 'transparent'; });
    back.addEventListener('click', () => {
      this._stopPlayback();
      this._showGrid();
    });

    // Player frame
    const frame = document.createElement('div');
    frame.style.cssText = `
      width: 100%;
      aspect-ratio: 16 / 9;
      background: #000;
      border-radius: 6px;
      overflow: hidden;
    `;

    const media = this._buildMediaElement(item);
    if (media) frame.appendChild(media);

    // Title strip below the video
    const titleStrip = document.createElement('div');
    titleStrip.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 4px 2px 0;
    `;
    const t = document.createElement('div');
    t.textContent = item.title;
    t.style.cssText = `
      font-size: 13px;
      color: #f4ead5;
    `;
    titleStrip.appendChild(t);
    if (item.description) {
      const d = document.createElement('div');
      d.textContent = item.description;
      d.style.cssText = `
        font-size: 11px;
        color: rgba(244, 234, 213, 0.55);
        line-height: 1.5;
      `;
      titleStrip.appendChild(d);
    }

    this.playerEl.appendChild(back);
    this.playerEl.appendChild(frame);
    this.playerEl.appendChild(titleStrip);

    this._showPlayer();
  }

  _buildMediaElement(item) {
    switch (item.embedKind) {
      case 'mp4': {
        const v = document.createElement('video');
        v.src = item.embedUrl;
        v.controls = true;
        v.autoplay = true;
        v.style.cssText = 'width: 100%; height: 100%; display: block; background: #000;';
        return v;
      }
      case 'vimeo': {
        // Convert standard Vimeo URLs to player.vimeo.com/video/<id> if needed
        const src = this._vimeoEmbedUrl(item.embedUrl);
        return this._iframe(src);
      }
      case 'youtube': {
        const src = this._youtubeEmbedUrl(item.embedUrl);
        return this._iframe(src);
      }
      case 'link': {
        // Fallback: just open in a new tab and show a placeholder in the modal
        window.open(item.embedUrl, '_blank', 'noopener,noreferrer');
        const note = document.createElement('div');
        note.textContent = 'Opened in a new tab.';
        note.style.cssText = `
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(244, 234, 213, 0.7);
          font-size: 12px;
        `;
        return note;
      }
      default:
        return null;
    }
  }

  _iframe(src) {
    const f = document.createElement('iframe');
    f.src = src;
    f.allow = 'autoplay; fullscreen; picture-in-picture';
    f.allowFullscreen = true;
    f.style.cssText = 'width: 100%; height: 100%; border: 0; display: block;';
    return f;
  }

  _vimeoEmbedUrl(url) {
    // Accept either a full vimeo.com/<id> URL or a player.vimeo.com URL as-is.
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
    return url;
  }

  _youtubeEmbedUrl(url) {
    // Accept watch?v=<id>, youtu.be/<id>, or already-embedded URLs.
    const watch = url.match(/[?&]v=([\w-]+)/);
    const short = url.match(/youtu\.be\/([\w-]+)/);
    const id = watch ? watch[1] : short ? short[1] : null;
    if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
    return url;
  }

  _stopPlayback() {
    // Empty the player element so any <video> stops + iframes unload
    if (this.playerEl) this.playerEl.innerHTML = '';
  }

  _bindKeys() {
    if (this._keyHandler) return;
    this._keyHandler = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  _unbindKeys() {
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }
}
